<template>
  <div ref="sessionCardRef" class="session-card">
    <ul class="session-card__list">
      <li
        v-for="(session, index) in sessionList"
        :key="index"
        :ref="(element) => setMessageRef(element, index)"
        class="session-card__list--item"
        :class="{
          'session-card__list--item-active': activeMessageIndex === index,
          'session-card__list--item-has-suggestion': session.badInfo
        }"
        @animationend="handleActiveAnimationEnd(index, $event)"
      >
        <div class="session-header">
          <template v-if="isCustomerRole(session.role)">
            <img class="agent-avatar" :src="require('@/assets/ai-avatar/ai-avatar-2x-zhang.png')" />
            <span class="session-header__name">{{ dataInfo.customerName || '张经理' }}</span>
            <span class="session-header__time">{{ session.time }}</span>
          </template>
          <template v-else>
            <div class="user-avatar">{{ dataInfo.userName?.charAt(0) }}</div>
            <span class="session-header__name">{{ dataInfo.userName }}</span>
            <span class="session-header__time">{{ session.time }}</span>
            <div v-if="session.badInfo" class="session-header__status">
              <span class="deduction-tag">扣分</span>
              <span class="deduction-score">{{
                formatDeductionScore(session.badInfo.deductionScore)
              }}</span>
            </div>
          </template>
        </div>
        <div class="session-content" :class="isCustomerRole(session.role) ? 'agent' : 'user'">
          {{ session.content }}
          <div v-if="session.badInfo?.suggestion" class="session-card__suggestion">
            <div class="session-card__suggestion--title">
              <i class="icon-sparkling-2-fill t-iconfont"></i>
              <span class="suggestion-label">AI 建议</span>
            </div>
            <div class="suggestion-content">{{ session.badInfo.suggestion }}</div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineExpose, nextTick, onUnmounted } from 'vue'
// props
const props = defineProps({
  dataList: {
    type: Array,
    default: () => []
  },
  dataInfo: {
    type: Object,
    default: () => ({})
  },
  badList: {
    type: Array,
    default: () => []
  }
})

const sessionCardRef = ref(null)
const messageRefs = ref([])
const activeMessageIndex = ref(-1)

const isCustomerRole = (role) => ['assistant', 'customer'].includes(role)

const formatDeductionScore = (score) => {
  const currentScore = Number(score)
  if (!Number.isFinite(currentScore)) return ''
  return `-${Math.abs(currentScore)}`
}

const badInfoMap = computed(() => {
  return props.badList.reduce((result, item) => {
    if (Number.isInteger(item?.index) && item.index >= 0) {
      result[item.index] = item
    }
    return result
  }, {})
})

const sessionList = computed(() => {
  return props.dataList.map((session, index) => ({
    ...session,
    badInfo: badInfoMap.value[index] || null
  }))
})

const setMessageRef = (element, index) => {
  if (!element) return
  messageRefs.value[index] = element
}

const clearActiveState = () => {
  activeMessageIndex.value = -1
}

const startActiveState = (messageIndex) => {
  clearActiveState()
  nextTick(() => {
    activeMessageIndex.value = messageIndex
  })
}

const handleActiveAnimationEnd = (messageIndex, event) => {
  if (event?.animationName !== 'session-item-border-flash') return
  if (activeMessageIndex.value !== messageIndex) return
  activeMessageIndex.value = -1
}

const scrollToMessage = (messageIndex) => {
  if (!Number.isInteger(messageIndex) || messageIndex < 0) return
  const targetElement = messageRefs.value[messageIndex]
  if (!targetElement || typeof targetElement.scrollIntoView !== 'function') return

  targetElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  })
  startActiveState(messageIndex)
}

onUnmounted(() => {
  clearActiveState()
})

defineExpose({
  scrollToMessage
})
</script>

<style lang="less" scoped>
.session-card {
  &__list {
    display: flex;
    flex-direction: column;

    &--item + &--item {
      margin-top: 16px;
    }

    &--item {
      position: relative;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        background-color 0.2s ease;

      &-active {
        &::after {
          content: '';
          position: absolute;
          top: -8px;
          right: -8px;
          bottom: -8px;
          left: -8px;
          border: 1px solid #f54e4e;
          border-radius: 16px;
          pointer-events: none;
          opacity: 0;
          animation: session-item-border-flash 1.25s ease-in-out 4;
        }
      }

      .session-header {
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 24px;
        line-height: 24px;
        margin-bottom: 8px;
        flex-wrap: wrap;

        > * + * {
          margin-left: 8px;
        }

        &__name {
          font-size: 14px;
          color: #646566;
        }
        &__time {
          font-size: 14px;
          color: #969799;
        }

        &__status {
          display: flex;
          align-items: center;
          margin-left: auto;

          .deduction-tag {
            padding: 2px 8px;
            border-radius: 8px;
            background: #ffece8;
            font-size: 12px;
            line-height: 16px;
            color: #f54e4e;
          }

          .deduction-score {
            margin-left: 4px;
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;
            color: #f54e4e;
          }
        }

        .agent-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
        }

        .user-avatar {
          width: 24px;
          height: 24px;
          text-align: center;
          line-height: 24px;
          border-radius: 50%;
          background: #248df5;
          color: #fff;
        }
      }
      .session-content {
        padding: 12px;
        width: 100%;
        line-height: 20px;
        border-radius: 12px;
        color: #323233;
        white-space: pre-wrap;
        word-break: break-word;

        &.agent {
          background: #f2f3f5;
        }
        &.user {
          background: #f5eefa;
          border: 1px solid transparent;
        }
      }
    }
  }

  &__suggestion {
    margin-top: 10px;
    padding: 12px;
    border: 1px solid #f5e8ff;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.05);

    &--title {
      display: flex;
      align-items: center;
    }

    .t-iconfont {
      font-size: 18px;
      text-align: center;
      background-clip: text;
      color: transparent;
      background-image: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    }

    .suggestion-label {
      margin-left: 8px;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      background-clip: text;
      color: transparent;
      background-image: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    }

    .suggestion-content {
      margin-top: 4px;
      font-size: 13px;
      color: #323233;
      line-height: 18px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}

@keyframes session-item-border-flash {
  0%,
  100% {
    opacity: 0;
  }

  50% {
    opacity: 1;
  }
}
</style>
